// ═══════════════════════════════════════════════════════
// TWO-FACTOR SETUP — Full enrollment flow with backend
// Steps: Generate QR → Verify Code → Show Backup Codes → Done
// ═══════════════════════════════════════════════════════
import { useState, useEffect, type FormEvent } from "react";
import { type ThemeObject, SP, RADIUS, SHADOW, TYPE } from "../shared/theme.js";
import { supabase } from "../supabaseClient.js";
import { logError } from "../shared/logger.js";

interface TwoFactorSetupProps {
  T: ThemeObject;
  userId: string;
  userEmail?: string;
  onComplete?: () => void;
}

type SetupStep = "loading" | "qr" | "verify" | "backup" | "success";

/** QR Code display — renders otpauth URI as image via Google Charts API fallback */
function QRCodeDisplay({ uri, T }: { uri: string; T: ThemeObject }) {
  // Use Google Charts QR API for rendering (no npm dependency needed)
  const qrImageUrl = `https://chart.googleapis.com/chart?cht=qr&chs=200x200&chl=${encodeURIComponent(uri)}&choe=UTF-8`;

  return (
    <div
      style={{
        background: "#fff",
        padding: SP[4],
        borderRadius: RADIUS.md,
        textAlign: "center",
        marginBottom: SP[4],
        border: `2px solid ${T.border}`,
      }}
    >
      <img
        src={qrImageUrl}
        alt="QR Code para autenticação 2FA"
        width={200}
        height={200}
        style={{ display: "block", margin: "0 auto", borderRadius: RADIUS.sm }}
        onError={(e) => {
          // Fallback: show URI as text if image fails
          (e.target as HTMLImageElement).style.display = "none";
          const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
          if (fallback) fallback.style.display = "block";
        }}
      />
      <div
        style={{
          display: "none",
          fontSize: TYPE.xs,
          color: "#666",
          wordBreak: "break-all",
          fontFamily: "monospace",
          padding: SP[2],
          background: "#f5f5f5",
          borderRadius: RADIUS.sm,
          marginTop: SP[2],
        }}
      >
        {uri}
      </div>
      <p style={{ fontSize: TYPE.sm, color: "#64748B", margin: `${SP[3]} 0 0` }}>
        Escaneie com Google Authenticator, Authy ou Microsoft Authenticator
      </p>
    </div>
  );
}

export default function TwoFactorSetup({
  T,
  userId: _userId,
  userEmail: _userEmail,
  onComplete,
}: TwoFactorSetupProps) {
  const [step, setStep] = useState<SetupStep>("loading");
  const [otpauthUrl, setOtpauthUrl] = useState("");
  const [secretBase32, setSecretBase32] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Step 1: Generate secret on mount ──────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: fnErr } = await supabase.functions.invoke("setup-totp", {
          body: { action: "generate" },
        });

        if (cancelled) return;

        if (fnErr) throw fnErr;
        if (!data?.ok) throw new Error(data?.error || "Falha ao gerar QR code");

        setOtpauthUrl(data.otpauth_url || data.qr_data);
        setSecretBase32(data.secret);
        setBackupCodes(data.backup_codes || []);
        setStep("qr");
      } catch (err) {
        if (!cancelled) {
          logError("TwoFactorSetup", err as Error, { context: "generate" });
          setError((err as Error).message);
          setStep("qr"); // Show error state
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Step 2: Verify code ───────────────────────────────────
  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (verificationCode.length !== 6 || !/^\d+$/.test(verificationCode)) {
        throw new Error("Código deve ter 6 dígitos");
      }

      const { data, error: fnErr } = await supabase.functions.invoke("setup-totp", {
        body: { action: "verify", code: verificationCode },
      });

      if (fnErr) throw fnErr;
      if (!data?.ok) throw new Error(data?.error || "Código inválido");

      setStep("backup");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: Confirm backup codes saved ────────────────────
  const handleComplete = () => {
    setStep("success");
    onComplete?.();
  };

  // ── Copy backup codes ─────────────────────────────────────
  const handleCopyBackup = () => {
    const text = backupCodes.join("\n");
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  // ── Render ────────────────────────────────────────────────

  if (step === "success") {
    return (
      <div
        style={{
          textAlign: "center",
          padding: SP[6],
          background: T.card,
          borderRadius: RADIUS.lg,
          border: `1px solid ${T.border}`,
          boxShadow: SHADOW.md,
        }}
      >
        <div style={{ fontSize: 48, marginBottom: SP[4] }}>✅</div>
        <h2
          style={{
            fontSize: TYPE.xl,
            fontWeight: TYPE.bold,
            color: T.text,
            margin: `0 0 ${SP[2]}`,
          }}
        >
          2FA Ativado!
        </h2>
        <p style={{ fontSize: TYPE.base, color: T.sub, margin: 0, lineHeight: TYPE.relaxed }}>
          Sua conta agora está protegida com autenticação de dois fatores. Na próxima vez que fizer
          login, será necessário informar o código do seu app de autenticação.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: T.card,
        borderRadius: RADIUS.lg,
        border: `1px solid ${T.border}`,
        boxShadow: SHADOW.md,
        padding: SP[6],
        maxWidth: 500,
      }}
    >
      <h2
        style={{ fontSize: TYPE.xl, fontWeight: TYPE.bold, color: T.text, margin: `0 0 ${SP[2]}` }}
      >
        Autenticação de Dois Fatores (2FA)
      </h2>
      <p
        style={{
          fontSize: TYPE.base,
          color: T.sub,
          margin: `0 0 ${SP[4]}`,
          lineHeight: TYPE.relaxed,
        }}
      >
        Proteja sua conta com TOTP. Escaneie o código QR com seu aplicativo de autenticação.
      </p>

      {error && (
        <div
          style={{
            background: "#DC262615",
            border: "1px solid #DC2626",
            borderRadius: RADIUS.md,
            padding: SP[3],
            marginBottom: SP[4],
            fontSize: TYPE.sm,
            color: "#DC2626",
          }}
        >
          {error}
        </div>
      )}

      {step === "loading" && (
        <div style={{ textAlign: "center", padding: SP[6], color: T.muted }}>
          Gerando chave de segurança...
        </div>
      )}

      {step === "qr" && (
        <>
          {otpauthUrl ? (
            <QRCodeDisplay uri={otpauthUrl} T={T} />
          ) : (
            <div style={{ textAlign: "center", padding: SP[4], color: T.muted }}>
              Não foi possível gerar o QR code. Verifique sua conexão.
            </div>
          )}

          {/* Manual secret entry fallback */}
          {secretBase32 && (
            <div
              style={{
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderRadius: RADIUS.sm,
                padding: SP[3],
                marginBottom: SP[4],
                textAlign: "center",
              }}
            >
              <p style={{ fontSize: TYPE.xs, color: T.muted, margin: `0 0 ${SP[1]}` }}>
                Ou insira manualmente:
              </p>
              <code
                style={{
                  fontSize: TYPE.sm,
                  color: T.text,
                  fontWeight: TYPE.bold,
                  letterSpacing: "2px",
                  userSelect: "all",
                }}
              >
                {secretBase32}
              </code>
            </div>
          )}

          <button
            onClick={() => {
              setError(null);
              setStep("verify");
            }}
            disabled={!otpauthUrl}
            style={{
              width: "100%",
              padding: `${SP[3]} ${SP[4]}`,
              background: otpauthUrl ? T.accent : T.muted,
              color: T.accentText,
              border: "none",
              borderRadius: RADIUS.md,
              fontSize: TYPE.md,
              fontWeight: TYPE.semibold,
              cursor: otpauthUrl ? "pointer" : "not-allowed",
            }}
          >
            Próximo: Verificar Código
          </button>
        </>
      )}

      {step === "verify" && (
        <form onSubmit={handleVerify}>
          <label
            style={{
              display: "block",
              fontSize: TYPE.md,
              fontWeight: TYPE.semibold,
              color: T.text,
              marginBottom: SP[2],
            }}
          >
            Digite os 6 dígitos do seu aplicativo:
          </label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            pattern="\d{6}"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            autoFocus
            placeholder="000000"
            style={{
              width: "100%",
              padding: SP[3],
              fontSize: "24px",
              textAlign: "center",
              letterSpacing: "8px",
              border: `2px solid ${T.border}`,
              borderRadius: RADIUS.md,
              color: T.text,
              background: T.surface,
              marginBottom: SP[4],
              fontFamily: "monospace",
              fontWeight: TYPE.semibold,
              boxSizing: "border-box",
            }}
          />
          <button
            type="submit"
            disabled={loading || verificationCode.length !== 6}
            style={{
              width: "100%",
              padding: `${SP[3]} ${SP[4]}`,
              background: verificationCode.length === 6 ? T.accent : T.muted,
              color: T.accentText,
              border: "none",
              borderRadius: RADIUS.md,
              fontSize: TYPE.md,
              fontWeight: TYPE.semibold,
              cursor: loading || verificationCode.length !== 6 ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Verificando..." : "Verificar"}
          </button>
        </form>
      )}

      {step === "backup" && (
        <>
          <h3
            style={{
              fontSize: TYPE.lg,
              fontWeight: TYPE.semibold,
              color: T.text,
              margin: `0 0 ${SP[2]}`,
            }}
          >
            Códigos de Backup
          </h3>
          <p
            style={{
              fontSize: TYPE.sm,
              color: T.sub,
              margin: `0 0 ${SP[4]}`,
              lineHeight: TYPE.relaxed,
            }}
          >
            Guarde esses códigos em um local seguro. Use um código se perder acesso ao seu app de
            autenticação. Cada código só pode ser usado uma vez.
          </p>
          <div
            style={{
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: RADIUS.md,
              padding: SP[4],
              marginBottom: SP[3],
            }}
          >
            {backupCodes.map((code, idx) => (
              <div
                key={idx}
                style={{
                  fontFamily: "monospace",
                  fontSize: TYPE.sm,
                  color: T.text,
                  padding: `${SP[2]} 0`,
                  borderBottom: idx < backupCodes.length - 1 ? `1px solid ${T.border}` : "none",
                  letterSpacing: "1px",
                }}
              >
                {code}
              </div>
            ))}
          </div>
          <button
            onClick={handleCopyBackup}
            style={{
              width: "100%",
              padding: `${SP[2]} ${SP[4]}`,
              background: "transparent",
              color: T.sub,
              border: `1px solid ${T.border}`,
              borderRadius: RADIUS.md,
              fontSize: TYPE.sm,
              cursor: "pointer",
              marginBottom: SP[3],
            }}
          >
            Copiar códigos
          </button>
          <button
            onClick={handleComplete}
            style={{
              width: "100%",
              padding: `${SP[3]} ${SP[4]}`,
              background: T.accent,
              color: T.accentText,
              border: "none",
              borderRadius: RADIUS.md,
              fontSize: TYPE.md,
              fontWeight: TYPE.semibold,
              cursor: "pointer",
            }}
          >
            Concluir Configuração
          </button>
        </>
      )}
    </div>
  );
}
